import { z } from "zod/v4";
import { createEndpointBase } from "./core";
import { SchedulerSchemas, SchedulerGroupSchemas } from "../ea.schema";
import { CountResponseSchema } from "../ea.schema.endpoints";
import {
  GetRequestBaseResponseSchema,
  SchedulerFilterSchema,
  SchedulerGroupFilterSchema,
} from "../ea.schema.query";

const base = "schedulers";
const schedulers = createEndpointBase(base);
const groups = createEndpointBase(base, "groups");

export const schedulersEndpoints = {
  // List + CRUD
  schedulersGet: schedulers({
    method: "GET",
    path: "",
    request: z.object({ filter: SchedulerFilterSchema.optional() }).optional(),
    response: GetRequestBaseResponseSchema(SchedulerSchemas["general"]),
    description: "Retrieves a list of execution schedules with filtering support.",
  }),
  schedulersCount: schedulers({
    method: "GET",
    path: "count",
    request: z.any(),
    response: CountResponseSchema,
    description: "Returns the total number of execution schedules matching the filters.",
  }),
  schedulersCreate: schedulers({
    method: "POST",
    path: "",
    request: SchedulerSchemas["createData"],
    response: z.any(),
    description: "Creates a new execution schedule.",
  }),
  schedulersUpdate: schedulers({
    method: "PATCH",
    path: ":id",
    request: SchedulerSchemas["update"],
    response: z.any(),
    description: "Updates an existing execution schedule.",
  }),

  // Actions
  schedulersClone: schedulers({
    method: "POST",
    path: "clone",
    request: z.object({ schedulerId: z.number(), name: z.string() }),
    response: z.any(),
    description: "Clones an existing execution schedule with a new name.",
  }),
  schedulersClose: schedulers({
    method: "POST",
    path: "close",
    request: z.object({ ids: z.array(z.number()) }),
    response: z.any(),
    description: "Closes (deactivates) one or more execution schedules.",
  }),
  schedulersCheckProjectRun: schedulers({
    method: "GET",
    path: "check-project",
    request: z.object({ projectId: z.coerce.number() }),
    response: z.object({ isRunning: z.boolean() }),
    description: "Checks if a specific project is currently being executed by a scheduler.",
  }),
  schedulersRunProject: schedulers({
    method: "POST",
    path: "run-project",
    request: z.object({ projectId: z.coerce.number() }),
    response: z.object({ jobId: z.string() }),
    description: "Triggers a project execution and returns a job ID.",
  }),
  schedulersTerminateProject: schedulers({
    method: "POST",
    path: "terminate-project",
    request: z.object({ projectId: z.coerce.number() }),
    response: z.any(),
    description: "Terminates all running executions for a specific project.",
  }),
  schedulersRunNow: schedulers({
    method: "POST",
    path: ":id/run",
    request: z.object({ schedulerId: z.number() }),
    response: z.any(),
    description: "Triggers an execution schedule to run immediately.",
  }),

  // Groups
  schedulersGroupsGet: groups({
    method: "GET",
    path: "",
    request: z.object({ filter: SchedulerGroupFilterSchema.optional() }).optional(),
    response: GetRequestBaseResponseSchema(SchedulerGroupSchemas["general"]),
  }),
  schedulersGroupsDelete: groups({
    method: "DELETE",
    path: ":id",
    request: z.object({ id: z.number() }),
    response: z.any(),
  }),
  schedulersGroupsDeleteAll: groups({
    method: "DELETE",
    path: "",
    request: z.object({
      schedulerId: z.number(),
      groupIds: z.array(z.number()),
      scriptIds: z.array(z.number()),
    }),
    response: z.any(),
  }),
  schedulersGroupsUpdate: groups({
    method: "POST",
    path: "",
    request: z.object({
      schedulerId: z.number(),
      groups: z.array(z.any()),
      replaceExisting: z.boolean().optional(),
    }),
    response: z.any(),
  }),
  schedulersGroupsReplace: groups({
    method: "POST",
    path: "replace",
    request: z.object({ schedulerId: z.number(), groups: z.array(z.any()) }),
    response: z.any(),
  }),
  schedulersGroupsScripts: groups({
    method: "POST",
    path: "scripts",
    request: z.object({ groupId: z.number(), scripts: z.array(z.any()) }),
    response: z.any(),
  }),
} as const;
